import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { FiExternalLink } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import { InventoryCountApi } from "../../../../api/inventoryCount"
import { WareHouseService } from "../../../../client"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"

export const Route = createFileRoute("/_layout/WMS/inventory-count/dashboard")({
  component: InventoryCountPanelPage,
})

function InventoryCountPanelPage() {
  const now = new Date()
  const defaultTo = now.toISOString().slice(0, 10)
  const defaultFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)

  const [dateFrom, setDateFrom] = useState(defaultFrom)
  const [dateTo, setDateTo] = useState(defaultTo)
  const [warehouseId, setWarehouseId] = useState("")
  const [topN, setTopN] = useState("5")

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list", { includeArchived: false }],
    queryFn: () => WareHouseService.getAllWarehouses(false),
    retry: false,
  })

  const kpiQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "kpi",
      { dateFrom, dateTo, warehouseId },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getKPI({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        warehouse_id: warehouseId || undefined,
      }),
  })

  const alertsQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "alerts",
      "dashboard",
      { dateFrom, dateTo, warehouseId },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getAnomalyAlerts({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        warehouse_id: warehouseId || undefined,
      }),
  })

  const topAnomalies = useMemo(() => {
    const limit = Math.max(1, Number(topN) || 5)
    const spikes = (alertsQuery.data?.adjustment_spikes ?? []).map((row) => ({
      key: `spike-${row.product_id}-${row.warehouse_id}-${row.location_code}`,
      productLabel:
        [row.product_sku?.trim(), row.product_name?.trim()]
          .filter(Boolean)
          .join(" - ") || row.product_id,
      warehouseLabel:
        [row.warehouse_code?.trim(), row.warehouse_name?.trim()]
          .filter(Boolean)
          .join(" - ") || row.warehouse_id,
      locationCode: row.location_code,
      kind: "adjustment_spike",
      value:
        row.baseline_avg_qty <= 1e-9
          ? row.total_adjustment_qty
          : row.spike_ratio,
      detail:
        row.baseline_avg_qty <= 1e-9
          ? `${row.total_adjustment_qty.toFixed(2)} qty | brak baseline`
          : `${row.total_adjustment_qty.toFixed(2)} qty | ratio ${row.spike_ratio.toFixed(2)}x`,
    }))
    const recounts = (alertsQuery.data?.frequent_recounts ?? []).map((row) => ({
      key: `recount-${row.product_id}-${row.location_code}`,
      productLabel:
        [row.product_sku?.trim(), row.product_name?.trim()]
          .filter(Boolean)
          .join(" - ") || row.product_id,
      warehouseLabel: "—",
      locationCode: row.location_code,
      kind: "frequent_recount",
      value: row.recount_count,
      detail: `${row.recount_count} recounts / ${row.task_count} tasks`,
    }))

    return [...spikes, ...recounts]
      .sort((a, b) => b.value - a.value)
      .slice(0, limit)
  }, [alertsQuery.data, topN])

  const kpi = kpiQuery.data

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Panel inwentaryzacji"
        description="Jeden ekran KPI inwentaryzacji i szybki przegląd anomalii."
        gradient="linear(to-r, teal.600, cyan.700)"
        badge="Panel"
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <WmsListToolbar
          filters={
            <>
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                borderRadius="xl"
              />
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                borderRadius="xl"
              />
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "260px" }}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie magazyny</option>
                {(warehousesQuery.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} - {warehouse.name}
                  </option>
                ))}
              </Select>
            </>
          }
          actions={
            <Flex align="center" gap={2} wrap="nowrap">
              <Select
                size="sm"
                w={{ base: "100%", lg: "150px" }}
                value={topN}
                onChange={(event) => setTopN(event.target.value)}
                borderRadius="xl"
              >
                <option value="3">Top 3</option>
                <option value="5">Top 5</option>
                <option value="10">Top 10</option>
              </Select>
              <Tooltip label="Alerty inwentaryzacji" hasArrow>
                <IconButton
                  size="sm"
                  as={Link}
                  to="/WMS/reports/inventory-count-alerts"
                  aria-label="Otwórz stronę alertów"
                  icon={<FiExternalLink />}
                  variant="outline"
                />
              </Tooltip>
            </Flex>
          }
        />

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3} mb={4}>
          <KpiCard
            label="Dokładność"
            value={kpi ? `${kpi.accuracy_pct.toFixed(2)}%` : "—"}
            helper={
              kpi ? `${kpi.accurate_lines}/${kpi.counted_lines} pozycji` : ""
            }
            cardBorder={cardBorder}
            mutedText={mutedText}
          />
          <KpiCard
            label="Oczekujące na weryfikację"
            value={kpi ? String(kpi.pending_review_tasks) : "—"}
            helper={kpi ? `łącznie zadań: ${kpi.total_tasks}` : ""}
            cardBorder={cardBorder}
            mutedText={mutedText}
          />
          <KpiCard
            label="Wskaźnik ponownego liczenia"
            value={kpi ? `${kpi.recount_rate_pct.toFixed(2)}%` : "—"}
            helper={kpi ? `przeliczone: ${kpi.recounted_tasks}` : ""}
            cardBorder={cardBorder}
            mutedText={mutedText}
          />
          <KpiCard
            label="Wartość korekt"
            value={kpi ? kpi.adjustment_value_total.toFixed(2) : "—"}
            helper="odchylenia zaksięgowanych zadań"
            cardBorder={cardBorder}
            mutedText={mutedText}
          />
        </SimpleGrid>

        <Box flex="1" minH={0}>
          <Text fontWeight="semibold" mb={2}>
            Najważniejsze anomalie (Top N)
          </Text>
          <WmsTableListCard
            isLoading={alertsQuery.isPending || kpiQuery.isPending}
            isFetching={alertsQuery.isFetching || kpiQuery.isFetching}
            error={
              alertsQuery.isError
                ? alertsQuery.error
                : kpiQuery.isError
                  ? kpiQuery.error
                  : undefined
            }
            errorMessage="Nie udało się pobrać dashboard inventory count."
            isEmpty={
              !alertsQuery.isPending &&
              !alertsQuery.isError &&
              topAnomalies.length === 0
            }
            emptyMessage="Brak anomalii dla wybranego okresu."
            minEmptyHeight={{ base: "180px", lg: "220px" }}
          >
            <Flex direction="column" gap={2}>
              {topAnomalies.map((row) => (
                <Flex
                  key={row.key}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="xl"
                  p={3}
                  align="center"
                  justify="space-between"
                  gap={3}
                  wrap="wrap"
                >
                  <Box>
                    <Flex gap={2} align="center" wrap="wrap">
                      <Badge
                        colorScheme={
                          row.kind === "adjustment_spike" ? "red" : "orange"
                        }
                      >
                        {row.kind}
                      </Badge>
                      <Text fontSize="sm">{row.detail}</Text>
                    </Flex>
                    <Text fontSize="sm" color={mutedText}>
                      produkt: {row.productLabel} | magazyn:{" "}
                      {row.warehouseLabel} | lokacja: {row.locationCode}
                    </Text>
                  </Box>
                  <Button
                    as={Link}
                    size="sm"
                    variant="outline"
                    to="/WMS/reports/inventory-count-alerts"
                  >
                    Sprawdź w alertach
                  </Button>
                </Flex>
              ))}
            </Flex>
          </WmsTableListCard>
        </Box>

        <Flex mt={4} justify="flex-end">
          <Stat maxW="320px">
            <StatLabel color={mutedText}>Zakres</StatLabel>
            <StatNumber fontSize="md">
              {dateFrom} - {dateTo}
            </StatNumber>
          </Stat>
        </Flex>
      </WmsPagePanel>
    </WmsPageShell>
  )
}

function KpiCard({
  label,
  value,
  helper,
  cardBorder,
  mutedText,
}: {
  label: string
  value: string
  helper: string
  cardBorder: string
  mutedText: string
}) {
  return (
    <Box borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={3}>
      <Stat>
        <StatLabel color={mutedText}>{label}</StatLabel>
        <StatNumber>{value}</StatNumber>
      </Stat>
      {helper ? (
        <Text fontSize="xs" color={mutedText}>
          {helper}
        </Text>
      ) : null}
    </Box>
  )
}
